import { useQueryClient } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router';

import { Breadcrumb } from '@shared/chrome/Breadcrumb';
import { formatMoney, formatNumber } from '@shared/format';
import { usePageParam } from '@shared/pagination';
import {
  DTable,
  DTableBody,
  DTableFoot,
  DTableHead,
  DTableRowHeader,
  DTableScroll,
  RowSkeleton,
} from '@shared/ui/dtable';
import { EmptyState } from '@shared/ui/EmptyState';
import { ShareBar } from '@shared/ui/ShareBar';
import { StatCard, StatGrid } from '@shared/ui/stat';

import { SurfaceError } from '../_shared/components';
import { sharedCopy } from '../_shared/copy';
import type { RevenueReport } from '../_shared/types';
import { copy } from './copy';
import { readFilters } from './filters';
import { errors } from './errors';
import { revenueReportKey, useRevenueReport, useSubscriptions } from './queries';
import { ReportingLedger } from './Reporting.Ledger';

const TABLE_COLUMNS = 3;
const TOTAL_TILE_KEY = 'total_amount';

/**
 * `/subscriptions` — subscription reporting.
 *
 * Two bands, two services, two independent reads. The aggregate band restates
 * the console entry's revenue section rather than recomputing it, because money
 * has one home and two surfaces reporting one datum with two values is drift.
 * The ledger beneath it is the record-level view, assembled in the money service
 * and nowhere else.
 *
 * **No chart, no axis, no legend, no second hue.** The design system puts charts
 * out of this version and names no chart shape, and the owning service ships no
 * chart band on the wire for the same reason. The share column is the documented
 * progress bar, in the indigo the design system assigns to subscription surfaces
 * by name — not a fifth identity colour and not a per-section palette.
 *
 * **No period control, no date range, no comparison.** The catalog's only date
 * facet belongs to the public search panel; borrowing one surface's setting onto
 * another is inventing configuration.
 *
 * **No export.** No row names one; the print stylesheet renders every table
 * whole on paper, which is the nearest thing the catalog does sanction.
 */
export function ReportingPage() {
  return (
    <>
      <Breadcrumb items={[{ label: sharedCopy.crumbs.console, to: '/' }, { label: copy.crumb }]} />

      <div className="mb-6 mt-3 grid gap-stack-tight">
        <h1 className="font-display text-h1 font-bold">{copy.head.title}</h1>
        <p className="mb-0 max-w-measure text-[1.0625rem] text-ink-muted">{copy.head.lede}</p>
      </div>

      <TotalsSection />
      <ReportingLedger />
    </>
  );
}

/**
 * «الحصيلة» — the four figures and the plan split.
 *
 * The currency word is read off a **ledger row** rather than held here: the
 * analytics payload carries an integer and no unit, and the row carries the
 * value written from the `CURRENCY` setting when the record was created. Where
 * no record exists yet there is no row to read, and the amount renders without a
 * unit rather than with one this console picked.
 */
function TotalsSection() {
  const query = useRevenueReport();
  const queryClient = useQueryClient();

  // Same query the ledger registers — one read, two readers, no second request.
  // The ordinal **and every facet** come from the same address the ledger reads
  // them from, so the two stay on one cache entry as the operator walks and as
  // they narrow; reading the ordinal alone would register a second key the
  // moment a filter was applied and issue the ledger's request twice. A page
  // with no row carrying a currency leaves the figure unitised rather than
  // borrowing one from an unfiltered page.
  const { page } = usePageParam();
  const [params] = useSearchParams();
  const ledger = useSubscriptions(page, readFilters(params));
  const currency = ledger.data?.items.find((row) => row.currency)?.currency ?? null;

  return (
    <section aria-labelledby="totals-heading">
      <div className="mb-4 flex flex-wrap items-center gap-x-3 gap-y-2">
        <h2 id="totals-heading" className="font-display text-h2 font-bold">
          {copy.totals.heading}
        </h2>
        <Link
          to="/plans"
          className="hit-target ms-auto flex items-center rounded-sm px-3 text-xs font-semibold text-ink hover:bg-hover-bg"
        >
          {copy.totals.link}
        </Link>
      </div>

      {query.isError ? (
        <SurfaceError
          error={query.error}
          catalog={errors}
          onRetry={() => void queryClient.invalidateQueries({ queryKey: revenueReportKey() })}
        />
      ) : null}

      {query.isPending ? (
        <>
          <p role="status" className="sr-only">
            {copy.states.loadingTotals}
          </p>
          <PlanTable>
            <RowSkeleton columns={TABLE_COLUMNS} />
          </PlanTable>
        </>
      ) : null}

      {query.data ? <TotalsData payload={query.data} currency={currency} /> : null}

      <p className="mb-0 mt-3 max-w-measure text-xs text-ink-muted">{copy.totals.grantNote}</p>
    </section>
  );
}

function TotalsData({ payload, currency }: { payload: RevenueReport; currency: string | null }) {
  const rows = payload.breakdown.rows;
  const total = payload.tiles.find((tile) => tile.key === TOTAL_TILE_KEY);

  /**
   * The whole each plan row is a share of. The service refuses a payload whose
   * rows do not sum to the stated total, so the tile is the honest denominator —
   * and where it is absent the rows are the whole, which is the same number.
   */
  const denominator = total?.value ?? rows.reduce((sum, row) => sum + row.value, 0);

  return (
    <>
      {payload.tiles.length > 0 ? (
        <StatGrid columns={4}>
          {payload.tiles.map((tile) => {
            const money = tile.key === TOTAL_TILE_KEY;

            return (
              <StatCard
                key={tile.key}
                label={copy.tiles[tile.key] ?? tile.key}
                value={<bdi>{formatNumber(tile.value)}</bdi>}
                // The unit sits under the figure, as the design draws it, and it
                // is read from the record rather than written into the copy.
                note={money && currency ? currency : undefined}
                // Eleven display-face glyphs do not fit half a 375 px window, and
                // the card carrying the amount is the one that has them. It spans
                // the row at the base tier and returns to its column from `md`.
                wide={money}
              />
            );
          })}
        </StatGrid>
      ) : null}

      {rows.length === 0 ? (
        <EmptyState message={copy.states.emptyTotals} />
      ) : (
        <PlanTable
          foot={
            total ? (
              <DTableFoot>
                <tr>
                  <th scope="row">{copy.totals.total}</th>
                  <td />
                  <td className="whitespace-nowrap">
                    <bdi>{currency ? formatMoney(total.value, { currency }) : formatNumber(total.value)}</bdi>
                  </td>
                </tr>
              </DTableFoot>
            ) : null
          }
        >
          {rows.map((row) => {
            const ratio = denominator > 0 ? row.value / denominator : 0;

            return (
              <tr key={row.key}>
                <DTableRowHeader>{row.label}</DTableRowHeader>
                <td className="w-[9rem]">
                  <ShareBar ratio={ratio} tone="subscription" />
                </td>
                <td className="whitespace-nowrap">
                  <bdi>{currency ? formatMoney(row.value, { currency }) : formatNumber(row.value)}</bdi>
                </td>
              </tr>
            );
          })}
        </PlanTable>
      )}
    </>
  );
}

/**
 * The plan-split frame, shared by the skeleton and the data states so the table
 * does not reflow when the payload arrives.
 *
 * The total is a `<tfoot>` rather than a last row: a total is neither a state
 * nor an action, and the table family gives it its own weight and rule without a
 * colour token that would imply one.
 */
function PlanTable({ children, foot }: { children: React.ReactNode; foot?: React.ReactNode }) {
  return (
    <DTableScroll>
      <DTable caption={copy.totals.caption} minInlineSize="30rem">
        <DTableHead>
          <tr>
            <th scope="col">{copy.totals.columns.plan}</th>
            <th scope="col" className="w-[9rem]">
              {copy.totals.columns.share}
            </th>
            <th scope="col">{copy.totals.columns.revenue}</th>
          </tr>
        </DTableHead>
        <DTableBody>{children}</DTableBody>
        {foot}
      </DTable>
    </DTableScroll>
  );
}
